
<template>

  <div class="wrapper">
    <div id="anchor-schritt-1" class="box-container bg-grey">
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-6">
            <figure class="image_container">
              <picture>
                <source srcset="files/calculator/schritt_01.webp" type="image/webp">
                <img src="files/calculator/schritt_01.jpg" width="800" height="700" alt="" loading="lazy" itemprop="image">
              </picture>
            </figure>
          </div>
          <div class="col-12 col-lg-6 box">
            <div>
              <span>Schritt 1</span>
              <h3>Stundensatz / Wartungsverträge</h3>
              <div>
                <p>Für die Berechnung benötigen wir deinen Stundensatz.</p>
                <div class="row">
                  <input class="col-7 col-sm-8" id="rs-range-line" type="range" min="3000" max="18000" step="1000" v-model="hourlyRate">
                  <label class="col-5 col-sm-4 font-color" for="rs-range-line">{{ formatPrice(hourlyRate) }}</label>
                </div>
              </div>
              <div>
                <p class="margin-top">Hast du bereits Wartungsverträge?</p>
                <input id="toggle-yes" class="toggle" type="radio" name="contracts" @change="hasContracts = true" checked><label for="toggle-yes">Ja</label><input id="toggle-no" class="toggle" type="radio" name="contracts" @change="hasContracts = false"><label for="toggle-no">Nein</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div class="box-container right">
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-6 order-lg-last">
            <figure class="image_container">
              <picture>
                <source srcset="files/calculator/schritt_02.webp" type="image/webp">
                <img src="files/calculator/schritt_02.jpg" width="800" height="700" alt="" loading="lazy" itemprop="image">
              </picture>
            </figure>
          </div>
          <div class="col-12 col-lg-6 order-lg-first box">
            <span>Schritt 2</span>
            <h3>Monatsrechnung / Installationen</h3>
            <template v-if="hasContracts">
              <p>Wie viel verrechnest du (im Durchschnitt) pro Monat?</p>
              <div>
                <div class="row">
                  <input class="col-7 col-sm-8" id="calculateContractPrice" type="range" min="500" max="8000" step="500" v-model="calculateContractPrice">
                  <label class="col-5 col-sm-4 font-color" for="calculateContractPrice">{{ formatPrice(calculateContractPrice) }}</label>
                </div>
              </div>
              <p class="margin-top">Wie viele Installationen mit Verträgen hast du?</p>
              <div class="quantity">
                <input id="paidInstallations" type="number" v-model="paidInstallations"/>
                <label for="paidInstallations"></label>
              </div>
            </template>
            <template v-else>
              <p>Wie lange brauchst du ungefähr für ein Contao Update?</p>
              <div class="quantity">
                <input id="calculateMinutes" type="number" v-model="calculateMinutes"/>
                <label for="calculateMinutes">min</label>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>



    <div class="box-container bg-grey result">
      <div class="container">
        <div class="row">
          <div class="col-12 offset-lg-2 col-lg-8">
            <div>
              <h2>Berechnungs-Ergebnis</h2>
              <template v-if="hasContracts">
                <p>Anhand deines Monatspreises schätzen wir, dass du mit <strong>ca. {{ recommendedMinutes }} Minuten Aufwand pro Update*</strong> kalkuliert hast.</p>
              </template>
              <template v-else>
                <p>
                  Basierend auf deinem Stundensatz und der geschätzen Arbeitsdauer*,
                  musst du für einen Wartungsvertrag <strong>mindestens {{ formatPrice(recommendedPricePerMonth) }}
                  pro Monat verlangen</strong>. Damit verdienst du gleich viel, wie wenn du in dieser Zeit an
                  Aufträgen gearbeitet hättest.
                </p>
                <p>
                  Mit <strong>Wartungsverträgen für</strong> <input type="number" v-model="paidInstallations">
                  <strong>Installationen</strong> erzielst du damit garantierte <strong>Einnahmen von {{ formatPrice(contractRevenue) }} pro Jahr</strong>.
                </p>
              </template>
              <template v-if="profitWithTrakked > 0">
                <p>
                  Mit trakked kannst du deinen <strong>Update-Aufwand</strong> geschätzt <strong>um zwei Drittel,
                  also ca. {{ downtimeWithTrakked }} Stunden pro Jahr reduzieren</strong>.
                  Wenn du in dieser Zeit <strong>verrechenbare Arbeit leistest</strong>, verdienst du mit trakked <strong>bis zu
                  {{ formatPrice(profitWithTrakked) }} zusätzlich</strong> pro Jahr (trakked-Abonnement eingerechnet).
                </p>
                <p>
                  Anders gerechnet: selbst wenn du deine <strong>Wartungsverträge für {{ formatPrice(trakkedPricePerMonth, 'ceil') }} pro Monat</strong> verrechnest,
                  wärst du <strong>mit trakked noch immer profitabel</strong>!
                </p>
              </template>
              <p v-else>
                Leider lohnt sich trakked.io für dich aktuell nicht.
              </p>
              <p><small>* Wir nehmen an, dass es durchschnittlich {{ updatesPerYear }} Contao-Updates pro Jahr gibt.</small></p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="anchor-kalkulator" class="box-container">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div class="details">
              <h2>Kalkulator für Wartungsverträge</h2>

              <div class="row">
                <div class="col-12 col-lg-6">
                  <h3>Angaben</h3>
                  <div class="row align-items-center">
                    <div class="col-6">Stundensatz</div>
                    <div class="col-3"><input type="range" min="3000" max="18000" step="1000" v-model="hourlyRate"></div>
                    <div class="col-3 font-color">{{ formatPrice(hourlyRate) }}</div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6">Monatsgebühr Wartungsvertrag</div>
                    <div class="col-3"><input type="range" min="500" max="8000" step="500" v-model="contractPricePerMonth"></div>
                    <div class="col-3 font-color">{{ formatPrice(contractPricePerMonth) }}</div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6">Installationen mit Vertrag</div>
                    <div class="col-6"><input type="number" v-model="paidInstallations"></div>
                  </div>
                </div>
                <div class="col-12 col-lg-6">
                  <h3>Annahmen</h3>
                  <div class="row align-items-center">
                    <div class="col-6 col-md-8">Contao Updates im Jahr</div>
                    <div class="col-6 col-md-4"><input type="number" v-model="updatesPerYear"></div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6 col-md-8">Aufwand ohne trakked</div>
                    <div class="col-6 col-md-4"><input type="number" v-model="minutesWithoutTrakked"> min</div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6 col-md-8">Aufwand mit trakked</div>
                    <div class="col-6 col-md-4"><input type="number" v-model="minutesWithTrakked"> min</div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6 col-md-8">getrakkte Installationen ohne Vertrag</div>
                    <div class="col-6 col-md-4"><input type="number" v-model="unpaidInstallations"></div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6 col-md-8">Updates für unbezahlte Installationen</div>
                    <div class="col-6 col-md-4"><input id="unpaid-no" class="toggle" name="unpaid" type="radio" @change="unpaidUpdates = false" checked><label for="unpaid-no">Nein</label><input id="unpaid-yes" class="toggle" name="unpaid" type="radio" @change="unpaidUpdates = true"><label for="unpaid-yes">Ja</label></div>
                  </div>

                </div>
              </div>


              <h3>Kalkulation für 12 Monate</h3>
              <div class="table-scrollable">
                <table class="sum">
                  <thead>
                  <tr>
                    <th></th>
                    <th>OHNE trakked</th>
                    <th>MIT trakked</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td>Garantierte Einnahmen</td>
                    <td style="text-align:right">{{ formatPrice(contractRevenue) }}</td>
                    <td style="text-align:right">{{ formatPrice(contractRevenue) }}</td>
                  </tr>
                  <tr>
                    <td>Aufwand für Updates</td>
                    <td style="text-align:right"><abbr :title="`${minutesWithoutTrakked}min x ${updatedInstallations} Installationen x ${updatesPerYear} Updates pro Jahr`">{{ Math.ceil(effortWithoutTrakked / 60) }}h</abbr> x {{ formatPrice(hourlyRate) }} = {{ formatPrice(costWithoutTrakked) }}</td>
                    <td style="text-align:right"><abbr :title="`${minutesWithTrakked}min x ${updatedInstallations} Installationen x ${updatesPerYear} Updates pro Jahr`">{{ Math.ceil(effortWithTrakked / 60) }}h</abbr> x {{ formatPrice(hourlyRate) }} = {{ formatPrice(costWithTrakked) }}</td>
                  </tr>
                  <tr>
                    <td>Ausgaben für trakked</td>
                    <td style="text-align:right">–</td>
                    <td style="text-align:right">{{ formatPrice(priceForTrakked) }}</td>
                  </tr>
                  </tbody>
                  <tfoot>
                  <tr>
                    <td>Mögliche Mehreinnahmen/-kosten</td>
                    <td style="text-align:right">{{ formatPrice(profitWithoutTrakked) }}</td>
                    <td style="text-align:right">{{ formatPrice(profitWithTrakked) }}</td>
                  </tr>
                  <tr>
                    <td><i>Alternativ:</i> Freizeit dank Verträgen</td>
                    <td style="text-align:right">
                      <template v-if="downtimeWithoutTrakked !== 0">ca. {{ downtimeWithoutTrakked }} Stunden</template>
                      <template v-else><i>keine</i></template>
                    </td>
                    <td style="text-align:right">
                      <template v-if="downtimeWithTrakked !== 0">ca. {{ downtimeWithTrakked }} Stunden</template>
                      <template v-else><i>keine</i></template>
                    </td>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

</template>

<script>
export default {

  data() {
    return {
      hasContracts: true,
      calculateContractPrice: 2000,
      calculateMinutes: 15,

      updatesPerYear: 12,
      minutesWithTrakked: 5,
      minutesWithoutTrakked: 15,
      contractPricePerMonth: 2000,
      pricePerInstallation: 249,
      hourlyRate: 8000,
      minimumInstallations: 10,
      paidInstallations: 10,
      unpaidInstallations: 0,
      unpaidUpdates: false,
    }
  },

  computed: {
    updatedInstallations: (vm) => Number(vm.paidInstallations) + (vm.unpaidUpdates ? Number(vm.unpaidInstallations) : 0),
    billedInstallations: (vm) => Math.max(vm.minimumInstallations, (Number(vm.paidInstallations) + Number(vm.unpaidInstallations))),
    contractRevenue: (vm) => vm.contractPricePerMonth * 12 * vm.paidInstallations,
    priceForTrakked: (vm) => 12 * vm.billedInstallations * vm.pricePerInstallation,

    effortWithTrakked: (vm) => vm.minutesWithTrakked * vm.updatedInstallations * vm.updatesPerYear,
    effortWithoutTrakked: (vm) => vm.minutesWithoutTrakked * vm.updatedInstallations * vm.updatesPerYear,

    costWithTrakked: (vm) => vm.hourlyRate / 60 * vm.effortWithTrakked,
    costWithoutTrakked: (vm) => vm.hourlyRate / 60 * vm.effortWithoutTrakked,

    profitWithTrakked: (vm) => vm.contractRevenue - vm.costWithTrakked - vm.priceForTrakked,
    profitWithoutTrakked: (vm) => vm.contractRevenue - vm.costWithoutTrakked,

    hourlyRateWithoutTrakked: (vm) => vm.contractRevenue / (vm.effortWithoutTrakked / 60),
    hourlyRateWithTrakked: (vm) => (vm.contractRevenue - vm.priceForTrakked) / (vm.effortWithTrakked / 60),

    downtimeWithTrakked: (vm) => Math.round(vm.profitWithTrakked / vm.hourlyRate),
    downtimeWithoutTrakked: (vm) => Math.round(vm.profitWithoutTrakked / vm.hourlyRate),

    recommendedPricePerMonth: (vm) => vm.hourlyRate / (60 / vm.minutesWithoutTrakked) * vm.updatesPerYear / 12,
    recommendedMinutes: (vm) => Math.ceil(60 / (vm.hourlyRate / (vm.calculateContractPrice * 12 / vm.updatesPerYear))),
    trakkedPricePerMonth: (vm) => vm.hourlyRate / (60 / vm.minutesWithTrakked) * vm.updatesPerYear / 12,
  },

  methods: {
    formatPrice(value, method = false) {
      value = method ? Math[method](value / 100) : (value / 100)

      if (Intl.NumberFormat) {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: 'EUR',
          currencyDisplay: 'symbol',
        }).format(value);
      }

      return `${value.toFixed(2)} €`;
    }
  },

  watch: {
    calculateContractPrice() {
      this.contractPricePerMonth = this.calculateContractPrice;

      this.minutesWithoutTrakked = this.recommendedMinutes;
      this.minutesWithTrakked = Math.max(Math.ceil(this.minutesWithoutTrakked / 3), this.minutesWithoutTrakked - 30);
    },

    calculateMinutes() {
      this.minutesWithoutTrakked = this.calculateMinutes;
      this.minutesWithTrakked = Math.max((this.minutesWithoutTrakked / 3), this.minutesWithoutTrakked - 30);

      this.contractPricePerMonth = this.recommendedPricePerMonth;
    },
  },
}
</script>
